import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { CalendarClock, Check, GitBranch, Link2, Plug, ShieldCheck, Sparkles, X, type LucideIcon } from 'lucide-react'
import { Link, useNavigate } from './nav'
import { requestChatPromptFill } from '../lib/chatPrompt'
import { useProjectSetupStatus, type ProjectSetupStatus, type SetupItemKey, type SetupItemState } from './useProjectSetupStatus'
import './ProjectSetupPanel.css'

const CALLOUT_KEY = 'project-setup-callout-dismissed'
const OPEN_EVENT = 'project-setup-panel:open'
/** Opens the checklist from outside the pill — the mobile "…" menu, where the pill doesn't fit. */
export function openProjectSetupPanel() { window.dispatchEvent(new Event(OPEN_EVENT)) }
const CALLOUT_WIDTH = 288
const SHORT_NAMES: Record<SetupItemKey, string> = {
  schedule: 'a test schedule', github: 'GitHub', security: 'security scans', agent: 'your coding agent', healing: 'Self-healing', tracker: 'an issue tracker',
}

interface SetupItem {
  key: SetupItemKey
  icon: LucideIcon
  title: string
  description: string
  to: string
  state: SetupItemState
  detail: string | null
  action: string
}

function hostOf(url: string | null | undefined) {
  if (!url) return null
  try { return new URL(url).host } catch { return null }
}

function setupItems(projectId: string, { project, cards, states, trackerName }: ProjectSetupStatus): SetupItem[] {
  const base = `/projects/${projectId}`
  const host = hostOf(project?.target_url)
  const detailFor = (key: SetupItemKey) => states[key] === 'todo' ? null : cards[key].text
  const action = (state: SetupItemState, labels: { todo: string; done: string; off?: string; waiting?: string }) =>
    state === 'done' ? labels.done : state === 'off' ? labels.off ?? 'Turn on' : state === 'waiting' ? labels.waiting ?? labels.done : labels.todo
  return [
    {
      key: 'schedule', icon: CalendarClock, to: `${base}/support/test-schedule`, state: states.schedule,
      title: 'Set a test run schedule',
      description: 'QA retests your app hourly, daily, or weekly.',
      detail: states.schedule === 'done' ? `Currently: ${cards.schedule.text}` : detailFor('schedule'),
      action: action(states.schedule, { todo: 'Set schedule', done: 'Edit' }),
    },
    {
      key: 'github', icon: GitBranch, to: `${base}/support/github`, state: states.github,
      title: 'Connect GitHub',
      description: 'QA tests every pull request and main-branch deployment.',
      detail: detailFor('github'),
      action: action(states.github, { todo: 'Connect GitHub', done: 'Manage' }),
    },
    {
      key: 'security', icon: ShieldCheck, to: `${base}/security`, state: states.security,
      title: host ? `Set up Security Scan for ${host}` : 'Set up Security Scan',
      description: 'Catch security vulnerabilities before your users do.',
      detail: detailFor('security'),
      action: action(states.security, { todo: 'Set up scan', done: 'View' }),
    },
    {
      key: 'agent', icon: Plug, to: `${base}/support/agent-connection`, state: states.agent,
      title: 'Connect your coding agent',
      description: 'Every bug QA finds goes straight to your agent with a full report.',
      detail: states.agent === 'done' && trackerName && cards.agent.tone !== 'white'
        ? `Bugs already go to ${trackerName}. You can connect an agent too.` : detailFor('agent'),
      action: action(states.agent, { todo: 'Connect agent', done: cards.agent.tone === 'white' ? 'Manage' : 'Connect' }),
    },
    {
      key: 'healing', icon: Sparkles, to: `${base}/support/self-healing`, state: states.healing,
      title: 'Turn on Self-healing',
      description: 'If you have visitors or customers, Self-healing turns problems in real user sessions into root-caused bug reports.',
      detail: detailFor('healing'),
      action: action(states.healing, { todo: 'Turn on', done: 'View sessions', waiting: 'Check status' }),
    },
    {
      key: 'tracker', icon: Link2, to: `${base}/support/issue-tracker`, state: states.tracker,
      title: 'Connect an issue tracker',
      description: 'Send new bugs where your team works: GitHub Issues, GitLab, Linear, Jira, or Asana.',
      detail: detailFor('tracker'),
      action: action(states.tracker, { todo: 'Connect tracker', done: 'Manage' }),
    },
  ]
}

const BADGES: Partial<Record<SetupItemState, ReactNode>> = {
  done: <><Check size={12} strokeWidth={2.5} aria-hidden />Set up</>,
  waiting: 'Waiting',
  off: 'Off',
  unknown: 'Checking…',
}

function ProgressRing({ done, total }: { done: number; total: number }) {
  const r = 7
  const c = 2 * Math.PI * r
  return <svg className="project-setup-ring" width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <circle cx="9" cy="9" r={r} className="project-setup-ring__track" />
    <circle cx="9" cy="9" r={r} className="project-setup-ring__value" strokeDasharray={`${(done / total) * c} ${c}`} transform="rotate(-90 9 9)" />
  </svg>
}

function useSetupProgress(projectId: string) {
  const status = useProjectSetupStatus(projectId)
  const items = setupItems(projectId, status)
  const done = items.filter(item => item.state === 'done').length
  return { status, items, done, complete: done === items.length, settled: items.every(item => item.state !== 'unknown') }
}

/** The mobile "…" menu's way into setup, standing in for the top-bar pill. */
export function ProjectSetupMenuItem({ projectId, onSelect }: { projectId: string; onSelect: () => void }) {
  const { status, items, done, complete } = useSetupProgress(projectId)
  if (!status.canEdit) return null
  return <button type="button" role="menuitem" className="chat-topbar__more-item project-setup-menu-item" onClick={onSelect} data-testid="chat-more-setup">
    {complete ? <Check size={16} strokeWidth={2.5} aria-hidden /> : <ProgressRing done={done} total={items.length} />}
    Setup
    <span className="project-setup-menu-item__count">{complete ? 'Done' : `${done}/${items.length}`}</span>
  </button>
}

/** Marks the mobile "…" button while setup is unfinished, so moving setup into the menu doesn't hide it. */
export function ProjectSetupMenuDot({ projectId }: { projectId: string }) {
  const { status, complete, settled } = useSetupProgress(projectId)
  if (!status.canEdit || !settled || complete) return null
  return <span className="project-setup-menu-dot" aria-hidden />
}

/** Top-bar "Setup n/6" pill and the floating checklist it opens. Floats over the project pane so the
 *  overview (and its live exploration) keeps its full height. `compact` drops the pill (and the edge tab) for
 *  the mobile header, which opens the checklist from its "…" menu instead; `onNavigate` runs when a step's
 *  page is opened, so the mobile shell can switch from the chat tab to the project tab. */
export default function ProjectSetupPanel({ projectId, autoOpen, compact = false, onNavigate }: {
  projectId: string; autoOpen: boolean; compact?: boolean; onNavigate?: () => void
}) {
  const { status, items, done, complete, settled } = useSetupProgress(projectId)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Stays true through the slide-out after `open` flips off, so the panel can animate away before unmounting.
  const [mounted, setMounted] = useState(false)
  useEffect(() => { if (open) setMounted(true) }, [open])
  const [top, setTop] = useState(56)
  const pillRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const seenKey = `project-setup-panel-seen:${projectId}`
  // Once someone has opened the checklist from the pill (or waved off the callout) they know where setup lives,
  // so this is per user rather than per project. Without storage, don't nag.
  const [calloutDismissed, setCalloutDismissed] = useState(() => {
    try { return localStorage.getItem(CALLOUT_KEY) === '1' } catch { return true }
  })
  const [calloutPos, setCalloutPos] = useState<{ top: number; left: number; arrow: number } | null>(null)
  const remaining = items.length - done
  const upNext = items.filter(item => item.state !== 'done').slice(0, 2).map(item => SHORT_NAMES[item.key])
  const showCallout = !open && !calloutDismissed && settled && !complete
  const dismissCallout = () => {
    setCalloutDismissed(true)
    try { localStorage.setItem(CALLOUT_KEY, '1') } catch { /* keep it dismissed for this session */ }
  }
  const anchorEl = () => pillRef.current ?? document.querySelector<HTMLElement>('[data-setup-anchor]')

  useEffect(() => {
    const onOpen = () => { setOpen(true); dismissCallout() }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  // First visit to an unfinished project's overview opens the checklist once; after that the pill is the way back.
  useEffect(() => {
    if (!autoOpen || !status.canEdit || !settled || complete) return
    try {
      if (localStorage.getItem(seenKey) === '1') return
      localStorage.setItem(seenKey, '1')
    } catch { return }
    setOpen(true)
  }, [autoOpen, status.canEdit, settled, complete, seenKey])

  useEffect(() => {
    if (!open) return
    const place = () => {
      const el = anchorEl()
      const anchor = el?.closest('header') ?? el
      if (anchor) setTop(anchor.getBoundingClientRect().bottom + 8)
    }
    place()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); pillRef.current?.focus() } }
    // The pill toggles the panel itself, so a press on it isn't an outside press.
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (!panelRef.current?.contains(target) && !pillRef.current?.contains(target)) setOpen(false)
    }
    window.addEventListener('resize', place)
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('resize', place)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open])

  useEffect(() => {
    if (!showCallout) return
    const place = () => {
      const rect = anchorEl()?.getBoundingClientRect()
      if (!rect || rect.width === 0) return setCalloutPos(null)
      const center = rect.left + rect.width / 2
      const left = Math.min(Math.max(center - CALLOUT_WIDTH / 2, 12), window.innerWidth - CALLOUT_WIDTH - 12)
      setCalloutPos({ top: rect.bottom + 10, left, arrow: center - left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [showCallout])

  const actionLabels = items.map(item => item.action).join('|')
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!open || !panel) return
    panel.style.removeProperty('--action-width')
    const widths = [...panel.querySelectorAll<HTMLElement>('.project-setup-item__action')].map(action => action.offsetWidth)
    if (widths.length) panel.style.setProperty('--action-width', `${Math.max(...widths)}px`)
  }, [open, actionLabels])

  if (!status.canEdit) return null

  return <>
    {!compact && <button
      ref={pillRef}
      type="button"
      className={`project-setup-pill${complete ? ' project-setup-pill--complete' : ''}${showCallout ? ' project-setup-pill--attention' : ''}`}
      onClick={() => { setOpen(v => !v); dismissCallout() }}
      aria-expanded={open}
      aria-controls="project-setup-panel"
      aria-label={complete ? 'Setup complete' : `Setup, ${done} of ${items.length} complete`}
      data-testid="project-setup-pill"
    >
      {complete ? <Check size={14} strokeWidth={2.5} aria-hidden /> : <ProgressRing done={done} total={items.length} />}
      <span className="project-setup-pill__label">Setup</span>
      {!complete && <span className="project-setup-pill__count">{done}/{items.length}</span>}
    </button>}
    {showCallout && calloutPos && <div
      className="project-setup-callout"
      role="dialog"
      aria-labelledby="project-setup-callout-title"
      style={{ top: calloutPos.top, left: calloutPos.left, width: CALLOUT_WIDTH, '--arrow-left': `${calloutPos.arrow}px` } as CSSProperties}
      data-testid="project-setup-callout"
    >
      <p id="project-setup-callout-title" className="project-setup-callout__title">
        {remaining} {remaining === 1 ? 'step' : 'steps'} left to get the most out of QA
      </p>
      <p>Next up: {upNext.join(' and ')}. Pick these up from {compact ? 'this menu' : 'Setup'} anytime.</p>
      <div className="project-setup-callout__actions">
        <button type="button" className="project-setup-callout__dismiss" onClick={dismissCallout}>Not now</button>
        <button type="button" className="project-setup-callout__open" onClick={() => { setOpen(true); dismissCallout() }}>Show me</button>
      </div>
    </div>}
    {!compact && !complete && !open && <div className="project-setup-edge" data-testid="project-setup-edge">
      {/* Mouse-only shortcut; keyboard and screen-reader users have the pill. */}
      <button type="button" className="project-setup-edge__tab" tabIndex={-1} aria-hidden onClick={() => { setOpen(true); dismissCallout() }}>
        <ProgressRing done={done} total={items.length} />
        <span className="project-setup-edge__label">Setup</span>
        <span className="project-setup-edge__count">{done}/{items.length}</span>
      </button>
    </div>}
    {(open || mounted) && <div
      ref={panelRef}
      id="project-setup-panel"
      className={`project-setup-panel${open ? '' : ' is-closing'}`}
      onAnimationEnd={e => { if (e.target === e.currentTarget && !open) setMounted(false) }}
      role="dialog"
      aria-label="Get more from QA"
      style={{ top, maxHeight: `calc(100dvh - ${top}px - 16px)` }}
      data-testid="project-setup-panel"
    >
      <header className="project-setup-panel__header">
        <div className="project-setup-panel__heading">
          <h2>{complete ? 'QA is fully set up' : 'Get more from QA'}</h2>
          <button type="button" className="project-setup-panel__close" onClick={() => setOpen(false)} aria-label="Close setup">
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className="project-setup-panel__progress" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }} aria-hidden>
          {items.map(item => <span key={item.key} className={item.state === 'done' ? 'is-done' : undefined} />)}
        </div>
        <p><strong>{done} of {items.length}</strong> set up · Set these up in any order.</p>
      </header>
      <ol className="project-setup-panel__items">
        {items.map(item => {
          const Icon = item.icon
          const primary = item.state === 'todo' || item.state === 'off'
          return <li key={item.key} className={`project-setup-item project-setup-item--${item.state}`}>
            <span className="project-setup-item__icon"><Icon size={16} aria-hidden /></span>
            <div className="project-setup-item__body">
              <div className="project-setup-item__title">
                <strong>{item.title}</strong>
                {BADGES[item.state] && <span className="project-setup-item__badge">{BADGES[item.state]}</span>}
              </div>
              <p>{item.description}</p>
              {item.detail && <p className="project-setup-item__detail">{item.detail}</p>}
              <Link
                to={item.to}
                className={`project-setup-item__action${primary ? ' project-setup-item__action--primary' : ''}`}
                onClick={e => {
                  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                  // Let the panel slide away before the page behind it changes, so the switch reads as a move
                  // to a new page rather than an instant swap under the panel.
                  e.preventDefault()
                  setOpen(false)
                  onNavigate?.()
                  window.setTimeout(() => navigate(item.to), 160)
                }}
              >
                {item.action}
              </Link>
            </div>
          </li>
        })}
      </ol>
      <footer className="project-setup-panel__footer">
        You can also <button type="button" onClick={() => { requestChatPromptFill('Help me finish setting up QA for this project.'); setOpen(false) }}>ask QA in chat</button> to set any of these up.
      </footer>
    </div>}
  </>
}
